// @givmo/board — wall-clock time in the MEETING's zone ⇄ the instant on the wire.
//
// ══ WHY THIS EXISTS AT ALL ══════════════════════════════════════════════════════════════════════
//
// An officer schedules "18:00 on 15 August" for a board that meets in New York, and that is what they
// mean whether they are typing it from New York, Honolulu, or Berlin. The wire carries an INSTANT, and
// the backend counts notice in calendar days localized to the MEETING's zone on both instants —
// deliberately never the scheduler's, because the count is a property of the organization's calendar. So
// this app must do the same conversion in the same direction: wall clock in the meeting's zone → the
// instant, and back again for editing.
//
// Taking the browser's zone here is the bug this module exists to prevent: it moves the meeting by
// hours, and — because the notice count is in calendar days — it can move the recorded notice figure
// by a whole day. The backend's own suite hit that class of error from the other side (fixtures
// stepped in UTC read as N+1 days for the four hours a day New York is still on the previous date).
//
// ══ THE ALGORITHM, AND WHY IT TAKES TWO PASSES ══════════════════════════════════════════════════
//
// JavaScript can format an instant INTO a zone but cannot parse a wall clock FROM one. The standard
// inversion: guess the instant by reading the wall clock as if it were UTC, measure what that zone
// actually shows at the guess, and subtract. A single pass is wrong across a DST transition, where the
// offset at the guess differs from the offset at the answer — so the correction is applied twice,
// which converges for every real zone.
//
// No new dependency: `Intl` is in every target runtime, and the repo does not take one for this.

/** A wall-clock date + time as the two HTML input values carry them. */
export interface ZonedFields {
  /** `YYYY-MM-DD`. */
  date: string;
  /** `HH:mm` (24-hour). */
  time: string;
}

/** The reader's own IANA zone, as the sensible default when scheduling a new meeting. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Every IANA zone this runtime knows, for the picker. Falls back to a short list of common zones on a
 * runtime without `Intl.supportedValuesOf` — a short list is better than an empty picker, and the
 * value is a free-text IANA name either way, so nothing is lost.
 */
export function availableTimeZones(): string[] {
  try {
    const supported = (
      Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    if (Array.isArray(supported) && supported.length > 0) return supported;
  } catch {
    // Fall through to the short list.
  }
  return [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'UTC',
  ];
}

/** Whether this runtime recognizes `timeZone` as an IANA zone. */
export function isUsableTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/**
 * The offset, in minutes, that `timeZone` is AHEAD of UTC at `instant`. New York in August → +(-240).
 */
function offsetMinutesAt(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  // `hour12: false` renders midnight as `24` in some engines; `% 24` normalizes it.
  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second'),
  );
  return (asIfUtc - instant.getTime()) / 60_000;
}

/**
 * Convert a wall clock in `timeZone` to the ISO instant the wire carries.
 *
 * Returns `null` for an unparseable date/time or an unusable zone, so a caller surfaces a real
 * validation message rather than sending an instant nobody chose.
 */
export function zonedFieldsToInstant(fields: ZonedFields, timeZone: string): string | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fields.date.trim());
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(fields.time.trim());
  if (dateMatch === null || timeMatch === null) return null;
  if (!isUsableTimeZone(timeZone)) return null;

  const [, year, month, day] = dateMatch.map(Number) as [unknown, number, number, number];
  const [, hour, minute] = timeMatch.map(Number) as [unknown, number, number];
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;

  const wallClockAsUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
  // Pass 1: correct by the offset at the naive guess.
  let instant = wallClockAsUtc - offsetMinutesAt(new Date(wallClockAsUtc), timeZone) * 60_000;
  // Pass 2: re-measure at the candidate answer, which is what makes DST transitions come out right.
  instant = wallClockAsUtc - offsetMinutesAt(new Date(instant), timeZone) * 60_000;

  const resolved = new Date(instant);
  return Number.isNaN(resolved.getTime()) ? null : resolved.toISOString();
}

/**
 * Render an ISO instant for READING, in the MEETING's zone rather than the reader's.
 *
 * ⚠ Not the same choice `display.ts`'s `displayInstant` makes, and the difference is deliberate. An
 * ordinary instant — when a record was approved, when an officer signed in — genuinely happened at a
 * moment, and the reader's own zone is how they experience it. But an act recorded AT a meeting belongs
 * to the meeting's calendar: an officer in the room at 8pm New York time recording a conflict link is
 * acting on the meeting's date, and rendering that instant in UTC (or in a remote reader's zone) dates
 * their act to the NEXT day — after the meeting it happened at.
 *
 * That is not hypothetical. The backend hit exactly this on the same data: `list_links` rendered its
 * statements with no meeting zone, so every link recorded in the evening dated the officer's act to the
 * following UTC day, on a read whose entire content is dates. The fix there made `time_zone` a REQUIRED
 * argument rather than a defaulted one, so an omission raises instead of producing a wrong date — and
 * this is the same rule on the client side: there is no zone-less arm, and no browser-zone fallback.
 *
 * Returns `—` for an absent or unparseable instant and for an unusable zone. Never throws, and never
 * silently falls back to a different zone: a wrong date in a minute book is worse than no date.
 */
export function displayInstantInZone(
  iso: string | null | undefined,
  timeZone: string | null | undefined,
): string {
  if (!iso || !timeZone) return '—';
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime()) || !isUsableTimeZone(timeZone)) return '—';
  return instant.toLocaleString(undefined, {
    timeZone,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * Render an ISO instant as the wall clock `timeZone` shows — the inverse, for editing a meeting whose
 * date was already set. Returns `null` when the instant or the zone is unusable.
 */
export function instantToZonedFields(
  iso: string | null | undefined,
  timeZone: string,
): ZonedFields | null {
  if (!iso) return null;
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime()) || !isUsableTimeZone(timeZone)) return null;

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant);

  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  const hour = String(Number(read('hour')) % 24).padStart(2, '0');
  return {
    date: `${read('year')}-${read('month')}-${read('day')}`,
    time: `${hour}:${read('minute')}`,
  };
}
